// If you don't have file-saver, install it: npm install file-saver
import { saveAs } from 'file-saver';

// Helper to get unique evidences by _id
function getUniqueEvidences(evidences: any[]): any[] {
  const map = new Map();
  evidences.forEach(ev => {
    if (ev && ev._id && !map.has(ev._id)) {
      map.set(ev._id, ev);
    }
  });
  return Array.from(map.values());
}

function getTodayString() {
  const d = new Date();
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
}

// --- COLUMN DEFINITIONS (from backend) ---
// Columns will be dynamically generated from flattenIdea and flattenEvidence outputs

// --- FLATTEN HELPERS ---
function flattenIdea(idea: any): Record<string, any> {
  console.log('flattenIdea input:', idea);
  return {
    id: idea._id,
    title: idea.title
  };
}

function flattenEvidence(ev: any): Record<string, any> {
  return {
    'company.name': ev.company?.name || null,
    'company.crm_id': ev.company?.crmId || null,
    'company.intercom_id': ev.company?.intercomId || null,
    'company.sf_id': ev.company?.sfId || null,
    'company.zendesk_id': ev.company?.zendeskId || null,
    'company.type': ev.company?.type || null,
    id: ev._id,
    created_at: ev.createdAt,
    title: ev.title || null,
    description: ev.text || null,
    excerpts: ev?.excerpts?.length > 0 ? '"' + [...ev?.excerpts.map((e: any) => e.excerpt)].join('"\n"') + '"' : null,
    origin: ev.origin,
    origin_id: ev.originId || null,
    type: ev.type,
    priority: ev.priority,
    domain: ev.componentObj?.domain,
    product_area: ev.componentObj?.name ?? ev.componentObj?.name, // to do check parent
    product_sub_area: ev.componentObj?.ch?.name ? ev.componentObj?.name : null,
    business_name: ev.businessName,
    source: ev.sourceOfAssignment,
    status: ev.state,
    'owner.name': ev.owner?.name || null,
    'owner.email': ev.owner?.email || null,
    'origin_object.origin': ev.cxItem?.origin || null,
    'origin_object.origin_id': ev.cxItem?.originId || null,
    'origin_object.type': ev.cxItem.type ? 'unknown' : null,
    'origin_object.title': ev.cxItem?.title || ev.cxItem?.name || null,
    'origin_object.link': ev.cxItem?.link || null,
    'submitter.name': ev.cxSubmitter?.name || null,
    'submitter.email': ev.cxSubmitter?.email || null
  };
}

// Main function
export function downloadIdeaToCSV({
  idea,
  allRequests = [],
  matches = [],
  requestsMatches = [],
  filename,
}: {
  idea: any,
  allRequests: any[],
  matches: any[],
  requestsMatches: any[],
  filename?: string,
}) {
  // 1. Filename
  const ideaId = idea?._id || 'unknown';
  const dateStr = getTodayString();
  const finalFilename = filename || `Idea_${ideaId}_${dateStr}.csv`;

  // 2. Idea headers and values (will be combined with evidence)
  const ideaFlat = flattenIdea(idea);
  const ideaColumns = Object.keys(ideaFlat);

  // 3. Evidence filtering
  const strongMatches = (matches || []).filter((item: any) => item?.match?.level?.toLowerCase() === 'strong');  
  const evidences = getUniqueEvidences([...allRequests, ...strongMatches]);

  // 4. Combined columns (Idea columns + Evidence columns)
  const evidenceColumns = evidences.length > 0 ? Object.keys(flattenEvidence(evidences[0])) : [];
  const combinedColumns = [...ideaColumns.map(col => `Idea.${col}`), ...evidenceColumns];
  const combinedHeaderLine = combinedColumns.join(',');

  // 5. Evidence rows with idea info prepended
  const evidenceRows = evidences.map(ev => {
    const flatEvidence = flattenEvidence(ev);
    return combinedColumns.map(col => {
      const val = col.startsWith('Idea.') ? ideaFlat[col.replace('Idea.', '')] : flatEvidence[col];
      if (val === undefined || val === null) return '';
      let v = String(val).replace(/"/g, '""');
      if (/[",\n]/.test(v)) v = `"${v}"`;
      return v;
    }).join(',');
  });

  // 6. Compose final CSV
  const csvContent = [
    combinedHeaderLine,
    ...evidenceRows
  ].join('\n');

  // 7. Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, finalFilename);
} 